import React, {useEffect, useState} from "react";
import {useNavigate, useParams} from "react-router-dom";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import {useAuth} from "../../../../hooks/useAuth";
import Loading from "../../../../shared/ui/Loading";
import CustomTimeline from "../../../../shared/ui/CustomTimeline";
import LinkStyled from "../../../../shared/ui/LinkStyled";
import {APITestLogs} from "../api/logs";
import TestItemCard, {AnswerChip} from "../../../../shared/ui/TestItemCard";
import {getRomanNumber} from "../../../../utils/functions";
import ReportHeader from "../../shared/components/ReportHeader";

const ACTION_DOT_COLORS: Record<string, string> = {
    saveAnswer: "primary.main",
    exitTestTab: "error.light",
    enterTestTab: "warning.main",
};

const getDotColor = (action: any): string => {
    return ACTION_DOT_COLORS[action] ?? "primary.main";
};

const findPreviousAnswerForItem = (logsList: any[], currentIndex: number, itemId: any) => {
    for (let i = currentIndex - 1; i >= 0; i--) {
        const prev = logsList[i];
        if (prev?.item?.id === itemId && prev?.answer != null) {
            return prev.answer;
        }
    }
    return null;
};

const mapLogs = (logsList: any[], testMode: any ) => {
    const logs = logsList.map((log, index) => {
        const fallbackAnswer = log.answer == null
            ? findPreviousAnswerForItem(logsList, index, log.item?.id)
            : null;

        const effectiveAnswer = log.answer != null ? log.answer : fallbackAnswer;

        return {
            action: log.action,
            date: log.action_at,
            title: log.action_description,
            dotColor: getDotColor(log.action),
            item: log.item ? {
                ...log.item,
                id: log.item.id,
                is_not_answered: effectiveAnswer == null,
                is_wrong_answer: effectiveAnswer != null && effectiveAnswer !== log.item.right_answer,
                is_right_answer: effectiveAnswer != null && effectiveAnswer === log.item.right_answer,
                user_answer: effectiveAnswer,
            } : null,
        }

    })

    return logs.map((log, index) => {
        return {
            ...log,
            item: log.item ? {
                bgColor: "transparent",
                border: "1px solid #D7E6FF",
                title: <Box display="flex">
                    <Typography mr={1} mb={0}>Item id: {log.item.id}</Typography>
                    <AnswerChip item={log.item} />
                </Box>,
                content: <TestItemCard
                    key={log.item.id ?? index}
                    question={log.item}
                    isCustomItemPreview={false}
                    testMode={testMode}
                    difficulty={log.item.difficulty}
                    isAnswerReport={true}
                    isPrinting={false}
                />,
            } : null,
        }
    })
}


const TestActivityReport = () => {
    const { id = "" } = useParams();
    const { update } = useAuth();
    const navigate = useNavigate();
    const [loading, setLoading] = useState<boolean>(true);
    const [logs, setLogs] = useState<any>();
    const [test, setTest] = useState<any>();

    useEffect(() => {
        setLoading(true);
        APITestLogs({ test: id })
            .then((response) => {
                if (response.data.success && response.data.data !== null) {
                    const logs = response.data.data.logs ? mapLogs(response.data.data.logs, response.data.data.test_mode.id) : [];
                    setLogs(logs);
                    setTest(response.data.data);
                } else if (response.data.success && response.data.data === null) {
                    navigate("/teste", { replace: true });
                } else {
                    update("error", { show: true, code: "A002" });
                }
            })
            .catch(() => {
                update("error", { show: true, code: "A001" });
            })
            .finally(() => {
                setLoading(false);
            })
    }, [id])

    if(loading) {
        return (
            <Box mt={3} display="flex" alignItems="center" width="100%">
                <Loading show={true}/>
            </Box>
        )
    }

    return (
        <>
            <ReportHeader
                locationUrl="raspunsuri"
                reportId={id}
                score={test.score}
                testDate={test.closing_date}
                testTypeId={test.test_mode.id}
                subjectName={test.discipline}
                className={test.grade_level}
            />

            {logs.length !== 0 ?(
                <CustomTimeline items={logs}/>
            ) : (
                <Typography variant="body1" mt={3}>
                    Nicio înregistrare găsită.
                </Typography>
            )}
        </>
    )
}

export default TestActivityReport;